import { Icon, Image } from '@aibees/shared/components';
import { LOOM_CREDIT_USAGE_GUIDE_EMBED } from '@aibees/shared/constants';
import { classnames } from '@aibees/shared/utils';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Modal,
  Stack,
  Typography,
} from '@mui/material';
import { memo, useState } from 'react';
import { CreditRules } from './components/credit-rules';
import { CreditCalculator } from './credit-calculator';
import * as Styled from './styles';
import * as SX from './sx';
import type { HowCreditsWorkProps as Props } from './types';

export const HowCreditsWork = memo(({ className }: Props) => {
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Styled.Wrapper
      className={classnames('HowCreditsWork', className)}
      sx={SX.root}
      variant="outlined"
    >
      <Typography variant="h6" color="grey.800" fontWeight={700}>
        How Credits Work?
      </Typography>
      <Typography variant="body4" color="grey.700" fontWeight={500}>
        Credits let you perform actions on our platform, with each task costing
        a different amount based on complexity.{' '}
      </Typography>
      <Card sx={SX.card}>
        <CardContent padding="extra" sx={SX.cardContent}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" alignItems="center" gap={1.5}>
              <Avatar
                size={48}
                src="/assets/images/avatar/CreditUsageGuide.png"
                variant="rounded"
              />
              <Stack direction="column">
                <Typography
                  variant="body3"
                  color="primary.500"
                  fontWeight="700"
                >
                  Credit Usage Guide
                </Typography>
                <Typography
                  variant="body4"
                  color="primary.300"
                  fontWeight="400"
                >
                  1 Minute Video
                </Typography>
              </Stack>
            </Stack>
            <Icon
              name="play-video"
              size={24}
              sxx={SX.icon}
              onClick={handleClickOpen}
            />
          </Stack>
        </CardContent>
      </Card>

      <Modal open={open} onClose={handleClose}>
        <Box sx={SX.modalBox}>
          <Box
            component="iframe"
            src={LOOM_CREDIT_USAGE_GUIDE_EMBED}
            title="Credit Usage Guide"
            allowFullScreen
            sx={SX.modalIframe}
          />
        </Box>
      </Modal>

      <CreditRules />

      <Stack gap={1} mt={1} mb={1}>
        <Typography variant="h6" color="grey.800" fontWeight={700}>
          Estimate Credit Usage
        </Typography>
        <Typography variant="body4" color="grey.700" fontWeight={500}>
          Calculate how many credits you will need on monthly basis.
        </Typography>
      </Stack>

      <Stack textAlign="center" gap={1}>
        <Stack direction="row" gap={1} justifyContent="center">
          <Typography variant="body3" color="grey.800" fontWeight={700} pt={1}>
            Estimate Monthly Credits
          </Typography>
          <Image src="/assets/images/vector-1.svg" />
        </Stack>
        <Box>
          <CreditCalculator />
        </Box>
      </Stack>
    </Styled.Wrapper>
  );
});

HowCreditsWork.displayName = 'HowCreditsWork';
